import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faPlus, faBuilding, faLayerGroup, faDoorOpen, faMapMarkerAlt, faChevronDown, faChevronUp, faWandMagicSparkles, faTrash, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { primaryColor, secondaryColor } from '../../../../utils/comon';
import Breadcrumbs from '../../../common/BreadCrum/BreadCrum';
import PageHeadingSection from '../../../common/pageheading/PageHeadingSection';
import { fetchLocationByPinCode, getCountries, getStatesByCountry, getCitiesByState } from '../../../../utils/locationService';
import CustomSelect from '../../../common/formfields/CustomSelect';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import apiServices from '../../../../services/apiService';
import storageService from '../../../../utils/storageService';

const SiteHierarchyBuilder = () => {
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);
    const [errors, setErrors] = useState({});
    const [bulkErrors, setBulkErrors] = useState({});

    // EXACT Payload structure in local state
    const [formData, setFormData] = useState(() => {
        try {
            const saved = storageService.getItem('siteHierarchyDraft');
            if (saved) {
                return saved;
            }
        } catch (e) {
            console.error("Failed to load draft", e);
        }
        return {
            siteName: '',
            pincode: '',
            countryName: '',
            stateName: '',
            cityName: '',
            createdBy: 'sys',
            isActive: 1, // 1 for Active, 0 for Inactive
            wings: []
        };
    });

    useEffect(() => {
        try {
            storageService.setItem('siteHierarchyDraft', formData);
        } catch (e) {
            console.error("Failed to save draft", e);
        }
    }, [formData]);

    const handlePincodeChange = async (e) => {
        const value = e.target.value;
        setFormData(prev => ({ ...prev, pincode: value }));

        if (value.length >= 6) {
            const res = await fetchLocationByPinCode(value);
            if (res.success && res.results && res.results.length > 0) {
                const location = res.results[0];
                setFormData(prev => ({
                    ...prev,
                    countryName: location.country || '',
                    stateName: location.state || '',
                    cityName: location.city || ''
                }));
            }
        }
    };

    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const fetchCountries = async () => {
            const data = await getCountries();
            setCountries(data);
        };
        fetchCountries();
    }, []);

    useEffect(() => {
        if (!formData.countryName) {
            setStates([]);
            return;
        }
        const fetchStates = async () => {
            const data = await getStatesByCountry(formData.countryName);
            setStates(data);
        };
        fetchStates();
    }, [formData.countryName]);

    useEffect(() => {
        if (!formData.stateName) {
            setCities([]);
            return;
        }
        const fetchCities = async () => {
            const data = await getCitiesByState(formData.countryName, formData.stateName);
            setCities(data);
        };
        fetchCities();
    }, [formData.countryName, formData.stateName]);

    const handleSelectChange = (key, option) => {
        setFormData(prev => {
            const newData = { ...prev, [key]: option ? option.value : null };
            if (key === 'countryName') {
                newData.stateName = null;
                newData.cityName = null;
            } else if (key === 'stateName') {
                newData.cityName = null;
            }
            return newData;
        });
        if (errors[key]) setErrors(prev => ({ ...prev, [key]: null }));
    };

    const [bulkConfig, setBulkConfig] = useState({
        numBuildings: '',
        numNormalFloors: '',
        numParkingFloors: '',
        numServiceFloors: '',
        numUnits: '',
        unitNumbering: '',
        wingsConfig: []
    });

    const handleNumberKeyDown = (e) => {
        if (['-', '+', 'e', 'E', '.'].includes(e.key)) {
            e.preventDefault();
        }
    };

    const handleNumBuildingsChange = (e) => {
        const val = e.target.value.replace(/\D/g, '');
        const count = parseInt(val) || 0;

        setBulkConfig(prev => {
            const newWingsConfig = [...prev.wingsConfig];
            if (count > newWingsConfig.length) {
                for (let i = newWingsConfig.length; i < count; i++) {
                    newWingsConfig.push({ name: `Building ${i + 1}`, status: 'Active' });
                }
            } else if (count < newWingsConfig.length) {
                newWingsConfig.splice(count);
            }
            return { ...prev, numBuildings: val, wingsConfig: newWingsConfig };
        });
    };

    const handleBulkGenerate = () => {
        const buildings = parseInt(bulkConfig.numBuildings) || 0;
        const normalFloors = parseInt(bulkConfig.numNormalFloors) || 0;
        const parkingFloors = parseInt(bulkConfig.numParkingFloors) || 0;
        const serviceFloors = parseInt(bulkConfig.numServiceFloors) || 0;
        const units = parseInt(bulkConfig.numUnits) || 0;

        const newBulkErrors = {};
        let hasErrors = false;
        if (!buildings || buildings <= 0) {
            newBulkErrors.numBuildings = "Required";
            hasErrors = true;
        }
        if (bulkConfig.numNormalFloors === '') {
            newBulkErrors.numNormalFloors = "Required";
            hasErrors = true;
        }
        if (bulkConfig.numParkingFloors === '') {
            newBulkErrors.numParkingFloors = "Required";
            hasErrors = true;
        }
        if (bulkConfig.numServiceFloors === '') {
            newBulkErrors.numServiceFloors = "Required";
            hasErrors = true;
        }
        if (bulkConfig.numUnits === '') {
            newBulkErrors.numUnits = "Required";
            hasErrors = true;
        }
        if (bulkConfig.unitNumbering === '') {
            newBulkErrors.unitNumbering = "Required";
            hasErrors = true;
        }
        bulkConfig.wingsConfig.forEach((wc, idx) => {
            if (!wc.name.trim()) {
                newBulkErrors[`wingName_${idx}`] = "Required";
                hasErrors = true;
            }
        });

        if (hasErrors) {
            setBulkErrors(newBulkErrors);
            return;
        }
        setBulkErrors({});

        if (buildings === 0) return;

        const getOrdinal = (n) => {
            const s = ["th", "st", "nd", "rd"];
            const v = n % 100;
            return n + (s[(v - 20) % 10] || s[v] || s[0]);
        };

        const generatedWings = [];
        for (let b = 1; b <= buildings; b++) {
            const generatedFloors = [];

            // 1. Generate Parking Floors
            for (let f = 1; f <= parkingFloors; f++) {
                generatedFloors.push({
                    id: `f-p-${b}-${f}-${Date.now()}`, // UI only
                    floorName: `P${f}`,
                    floorType: 'Parking',
                    CreatedBy: 'sys',
                    isActiveId: 1,
                    isExpanded: false, // UI only
                    units: []
                });
            }

            // 2. Generate Normal Floors
            for (let f = 1; f <= normalFloors; f++) {
                const floorDisplay = getOrdinal(f);
                const generatedUnits = [];

                for (let u = 1; u <= units; u++) {
                    let unitNumberStr = '';
                    if (bulkConfig.unitNumbering === 'Sequential') {
                        unitNumberStr = u.toString().padStart(2, '0');
                    } else if (bulkConfig.unitNumbering === 'Alphabetic') {
                        const letter = u <= 26 ? String.fromCharCode(64 + u) : `U${u}`;
                        unitNumberStr = `${f}${letter}`;
                    } else if (bulkConfig.unitNumbering === 'Floor-Prefixed-3Digit') {
                        unitNumberStr = `${f}${u.toString().padStart(3, '0')}`;
                    } else {
                        unitNumberStr = `${f}${u.toString().padStart(2, '0')}`;
                    }

                    generatedUnits.push({
                        id: `u-n-${b}-${f}-${u}-${Date.now()}`, // UI only
                        unitName: '',
                        unitNumber: unitNumberStr,
                        CreatedBy: 'sys',
                        isActive: 1
                    });
                }

                generatedFloors.push({
                    id: `f-n-${b}-${f}-${Date.now()}`, // UI only
                    floorName: floorDisplay,
                    floorType: 'Normal',
                    CreatedBy: 'sys',
                    isActiveId: 1,
                    isExpanded: false, // UI only
                    units: generatedUnits
                });
            }

            // 3. Generate Service Floors
            for (let f = 1; f <= serviceFloors; f++) {
                generatedFloors.push({
                    id: `f-s-${b}-${f}-${Date.now()}`, // UI only
                    floorName: `S${f}`,
                    floorType: 'Service',
                    CreatedBy: 'sys',
                    isActiveId: 1,
                    isExpanded: false, // UI only
                    units: []
                });
            }

            generatedWings.push({
                id: `w-${b}-${Date.now()}`, // UI only
                wingName: bulkConfig.wingsConfig[b - 1]?.name || `Building ${b}`,
                createdBy: 'sys',
                isActiveId: bulkConfig.wingsConfig[b - 1]?.status === 'Active' ? 1 : 0,
                isExpanded: true, // UI only
                floors: generatedFloors
            });
        }

        setFormData(prev => ({
            ...prev,
            wings: [...prev.wings, ...generatedWings]
        }));

        toast.success("Structural Hierarchy has been created.");
    };

    const addFloor = (wingIndex) => {
        const newWings = [...formData.wings];
        newWings[wingIndex].floors.push({ id: Date.now(), floorName: '', floorType: 'Normal', CreatedBy: 'sys', isActiveId: 1, isExpanded: true, units: [] });
        setFormData({ ...formData, wings: newWings });
    };

    const addUnit = (wingIndex, floorIndex) => {
        const newWings = [...formData.wings];
        newWings[wingIndex].floors[floorIndex].units.push({ id: Date.now(), unitName: '', unitNumber: '', CreatedBy: 'sys', isActive: 1 });
        setFormData({ ...formData, wings: newWings });
    };

    const removeWing = (wingIndex) => {
        const newWings = formData.wings.filter((_, idx) => idx !== wingIndex);
        setFormData({ ...formData, wings: newWings });
    };

    const removeFloor = (wingIndex, floorIndex) => {
        const newWings = [...formData.wings];
        newWings[wingIndex].floors = newWings[wingIndex].floors.filter((_, idx) => idx !== floorIndex);
        setFormData({ ...formData, wings: newWings });
    };

    const toggleWing = (wingIndex) => {
        const newWings = [...formData.wings];
        newWings[wingIndex].isExpanded = !newWings[wingIndex].isExpanded;
        setFormData({ ...formData, wings: newWings });
    };

    const toggleFloor = (wingIndex, floorIndex) => {
        const newWings = [...formData.wings];
        newWings[wingIndex].floors[floorIndex].isExpanded = !newWings[wingIndex].floors[floorIndex].isExpanded;
        setFormData({ ...formData, wings: newWings });
    };

    const handleSubmit = async () => {
        const newErrors = {};
        if (!formData.siteName.trim()) newErrors.siteName = "Site Name is required";
        if (!formData.pincode) newErrors.pincode = "Pincode is required";
        if (!formData.countryName) newErrors.countryName = "Country is required";
        if (!formData.stateName) newErrors.stateName = "State is required";
        if (!formData.cityName) newErrors.cityName = "City is required";

        formData.wings.forEach((wing, wIdx) => {
            wing.floors.forEach((floor, fIdx) => {
                if (!floor.floorName.trim()) {
                    newErrors[`floorName_${wIdx}_${fIdx}`] = "Required";
                    wing.isExpanded = true;
                    floor.isExpanded = true;
                }
                floor.units.forEach((unit, uIdx) => {
                    if (!unit.unitNumber.trim()) {
                        newErrors[`unitNumber_${wIdx}_${fIdx}_${uIdx}`] = "Required";
                        wing.isExpanded = true;
                        floor.isExpanded = true;
                    }
                });
            });
        });

        // Validate Bulk Config as well on final submit
        const newBulkErrors = {};
        let hasBulkErrors = false;
        const buildings = parseInt(bulkConfig.numBuildings) || 0;
        if (!buildings || buildings <= 0) {
            newBulkErrors.numBuildings = "Required";
            hasBulkErrors = true;
        }
        if (bulkConfig.numNormalFloors === '') {
            newBulkErrors.numNormalFloors = "Required";
            hasBulkErrors = true;
        }
        if (bulkConfig.numParkingFloors === '') {
            newBulkErrors.numParkingFloors = "Required";
            hasBulkErrors = true;
        }
        if (bulkConfig.numServiceFloors === '') {
            newBulkErrors.numServiceFloors = "Required";
            hasBulkErrors = true;
        }
        if (bulkConfig.numUnits === '') {
            newBulkErrors.numUnits = "Required";
            hasBulkErrors = true;
        }
        if (bulkConfig.unitNumbering === '') {
            newBulkErrors.unitNumbering = "Required";
            hasBulkErrors = true;
        }
        bulkConfig.wingsConfig.forEach((wc, idx) => {
            if (!wc.name.trim()) {
                newBulkErrors[`wingName_${idx}`] = "Required";
                hasBulkErrors = true;
            }
        });

        const hasMainErrors = Object.keys(newErrors).length > 0;

        if (hasMainErrors || hasBulkErrors) {
            if (hasMainErrors) setErrors(newErrors);
            if (hasBulkErrors) setBulkErrors(newBulkErrors);
            setFormData({ ...formData }); // re-render expansions
            toast.error("Please fill all required fields");
            return;
        }

        setErrors({});
        setBulkErrors({});

        setIsLoading(true);
        try {
            // Strip UI-only keys to perfectly match the strict backend payload
            const payload = {
                siteName: formData.siteName,
                countryName: formData.countryName,
                stateName: formData.stateName,
                cityName: formData.cityName,
                pincode: Number(formData.pincode),
                createdBy: formData.createdBy,
                isActive: formData.isActive,
                wings: formData.wings.map(wing => ({
                    wingName: wing.wingName,
                    createdBy: wing.createdBy,
                    isActiveId: wing.isActiveId,
                    floors: wing.floors.map(floor => ({
                        floorName: floor.floorName,
                        floorType: floor.floorType,
                        CreatedBy: floor.CreatedBy,
                        isActiveId: floor.isActiveId,
                        units: floor.units.map(unit => ({
                            unitName: unit.unitName,
                            unitNumber: unit.unitNumber,
                            CreatedBy: unit.CreatedBy,
                            isActive: unit.isActive
                        }))
                    }))
                }))
            };
            console.log(payload, "bulkpayload")

            const response = await apiServices({
                method: 'POST',
                url: '/SiteDetailsMaster/UnifiedBulkCreate',
                data: payload,
                useNodeApi: true
            });

            if (response.success && (response.data?.IsSuccess || response.data?.isSuccess || response.data?.success || response.IsSuccess)) {
                toast.success(response.data?.Message || "Site hierarchy successfully built!");
                storageService.removeItem('siteHierarchyDraft');
                navigate('/zentcomm/site-management/site');
            } else {
                toast.error(response.data?.Message || "Failed to create site hierarchy.");
            }
        } catch (error) {
            console.error("Submission error:", error);
            toast.error("An error occurred during submission.");
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="bg-dashboardbg px-6 py-4 border border-bordercolor flex flex-col rounded-lg flex-1 overflow-y-auto pt-4 scrollbar-hide">
            <Breadcrumbs />
            <PageHeadingSection
                Navbar={true}
                mainHeading="Manage Site Structure"
                subHeading="Create your complete property hierarchy automatically using the bulk generator, or build it manually."
            />

            <div className="flex-1 mt-6">
                {/* 1. Site Details Section */}
                <div className="p-6 rounded-2xl border border-blue-100 dark:border-bordercolor mb-6 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-[#FFEFEF] dark:bg-actionitembg text-[#E31F26] dark:text-textprimary flex items-center justify-center shadow-sm border border-blue-100 dark:border-bordercolor">
                            <FontAwesomeIcon icon={faMapMarkerAlt} className="text-lg" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">Site Details</h3>
                            <p className="text-xs text-gray-500">Define the main property location.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Site Name <span className="text-red-500">*</span></label>
                            <input
                                value={formData.siteName}
                                onChange={(e) => {
                                    setFormData({ ...formData, siteName: e.target.value });
                                    if (errors.siteName) setErrors(prev => ({ ...prev, siteName: null }));
                                }}
                                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.siteName ? 'border-red-500' : 'border-gray-200'}`} type="text" placeholder="e.g. Skyline Towers" />
                            {errors.siteName && <p className="text-red-500 text-[10px] mt-1">{errors.siteName}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Pincode <span className="text-red-500">*</span></label>
                            <input
                                value={formData.pincode}
                                onChange={(e) => {
                                    handlePincodeChange(e);
                                    if (errors.pincode) setErrors(prev => ({ ...prev, pincode: null }));
                                }}
                                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors.pincode ? 'border-red-500' : 'border-gray-200'}`} type="text" placeholder="Enter Pincode" />
                            {errors.pincode && <p className="text-red-500 text-[10px] mt-1">{errors.pincode}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Country <span className="text-red-500">*</span></label>
                            <CustomSelect
                                options={countries}
                                value={countries.find(c => c.value === formData.countryName) || null}
                                onChange={(opt) => handleSelectChange('countryName', opt)}
                                error={!!errors.countryName}
                            />
                            {errors.countryName && <p className="text-red-500 text-[10px] mt-1">{errors.countryName}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">State <span className="text-red-500">*</span></label>
                            <CustomSelect
                                options={states}
                                value={states.find(s => s.value === formData.stateName) || null}
                                onChange={(opt) => handleSelectChange('stateName', opt)}
                                disabled={!formData.countryName}
                                error={!!errors.stateName}
                            />
                            {errors.stateName && <p className="text-red-500 text-[10px] mt-1">{errors.stateName}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">City <span className="text-red-500">*</span></label>
                            <CustomSelect
                                options={cities}
                                value={cities.find(c => c.value === formData.cityName) || null}
                                onChange={(opt) => handleSelectChange('cityName', opt)}
                                disabled={!formData.stateName}
                                error={!!errors.cityName}
                            />
                            {errors.cityName && <p className="text-red-500 text-[10px] mt-1">{errors.cityName}</p>}
                        </div>
                        <div>
                            <label className="block text-xs font-bold text-gray-700 dark:text-textsecondary mb-1">Status</label>
                            <CustomSelect
                                options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                                value={{ label: formData.isActive === 1 ? 'Active' : 'Inactive', value: formData.isActive === 1 ? 'Active' : 'Inactive' }}
                                onChange={(opt) => setFormData({ ...formData, isActive: opt.value === 'Active' ? 1 : 0 })}
                            />
                        </div>
                    </div>
                </div>

                {/* 2. Bulk Generator Section */}
                <div className=" p-6 rounded-2xl border border-blue-100 dark:border-bordercolor mb-8 shadow-sm">
                    <div className="flex items-center gap-3 mb-6">
                        <div className="w-10 h-10 rounded-xl bg-[#FFEFEF] dark:bg-actionitembg text-[#E31F26] dark:text-textprimary flex items-center justify-center shadow-md">
                            <FontAwesomeIcon icon={faWandMagicSparkles} className="text-lg" />
                        </div>
                        <div>
                            <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">Bulk Automation Generator</h3>
                            <p className="text-xs text-gray-500">Instantly generate mixed-type buildings, floors, and units.</p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-6 gap-4 mb-4">
                        <div>
                            <label className="block text-[10px] font-bold text-gray-700 dark:text-textsecondary uppercase mb-1">Total Wings <span className="text-red-500">*</span></label>
                            <input
                                value={bulkConfig.numBuildings}
                                onKeyDown={handleNumberKeyDown}
                                onChange={(e) => {
                                    handleNumBuildingsChange(e);
                                    if (bulkErrors.numBuildings) setBulkErrors(prev => ({ ...prev, numBuildings: null }));
                                }}
                                min="0"
                                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:border-blue-500 ${bulkErrors.numBuildings ? 'border-red-500' : 'border-gray-200'}`} type="number" placeholder="e.g. 2" />
                            {bulkErrors.numBuildings && <p className="text-red-500 text-[10px] mt-1">{bulkErrors.numBuildings}</p>}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-blue-600 uppercase mb-1">Normal Floors <span className="text-red-500">*</span></label>
                            <input
                                value={bulkConfig.numNormalFloors}
                                onKeyDown={handleNumberKeyDown}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setBulkConfig({ ...bulkConfig, numNormalFloors: val });
                                    if (bulkErrors.numNormalFloors) setBulkErrors(prev => ({ ...prev, numNormalFloors: null }));
                                }}
                                min="0"
                                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:border-blue-500 ${bulkErrors.numNormalFloors ? 'border-red-500' : 'border-gray-200'}`} type="number" placeholder="e.g. 10" />
                            {bulkErrors.numNormalFloors && <p className="text-red-500 text-[10px] mt-1">{bulkErrors.numNormalFloors}</p>}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-amber-600 uppercase mb-1">Parking Floors <span className="text-red-500">*</span></label>
                            <input
                                value={bulkConfig.numParkingFloors}
                                onKeyDown={handleNumberKeyDown}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setBulkConfig({ ...bulkConfig, numParkingFloors: val });
                                    if (bulkErrors.numParkingFloors) setBulkErrors(prev => ({ ...prev, numParkingFloors: null }));
                                }}
                                min="0"
                                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:border-blue-500 ${bulkErrors.numParkingFloors ? 'border-red-500' : 'border-gray-200'}`} type="number" placeholder="e.g. 4" />
                            {bulkErrors.numParkingFloors && <p className="text-red-500 text-[10px] mt-1">{bulkErrors.numParkingFloors}</p>}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-emerald-600 uppercase mb-1">Service Floors <span className="text-red-500">*</span></label>
                            <input
                                value={bulkConfig.numServiceFloors}
                                onKeyDown={handleNumberKeyDown}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setBulkConfig({ ...bulkConfig, numServiceFloors: val });
                                    if (bulkErrors.numServiceFloors) setBulkErrors(prev => ({ ...prev, numServiceFloors: null }));
                                }}
                                min="0"
                                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:border-blue-500 ${bulkErrors.numServiceFloors ? 'border-red-500' : 'border-gray-200'}`} type="number" placeholder="e.g. 1" />
                            {bulkErrors.numServiceFloors && <p className="text-red-500 text-[10px] mt-1">{bulkErrors.numServiceFloors}</p>}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-700 dark:text-textsecondary uppercase mb-1">Units (Normal) <span className="text-red-500">*</span></label>
                            <input
                                value={bulkConfig.numUnits}
                                onKeyDown={handleNumberKeyDown}
                                onChange={(e) => {
                                    const val = e.target.value.replace(/\D/g, '');
                                    setBulkConfig({ ...bulkConfig, numUnits: val });
                                    if (bulkErrors.numUnits) setBulkErrors(prev => ({ ...prev, numUnits: null }));
                                }}
                                min="0"
                                className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:border-blue-500 ${bulkErrors.numUnits ? 'border-red-500' : 'border-gray-200'}`} type="number" placeholder="e.g. 5" />
                            {bulkErrors.numUnits && <p className="text-red-500 text-[10px] mt-1">{bulkErrors.numUnits}</p>}
                        </div>
                        <div>
                            <label className="block text-[10px] font-bold text-gray-700 dark:text-textsecondary uppercase mb-1">Unit Naming Style <span className="text-red-500">*</span></label>
                            <CustomSelect
                                options={[
                                    { label: 'Floor-Prefixed (e.g. 101)', value: 'Floor-Prefixed' },
                                    { label: 'Floor-Prefixed (e.g. 1001, 1002)', value: 'Floor-Prefixed-3Digit' },
                                    { label: 'Sequential (e.g. 01, 02)', value: 'Sequential' },
                                    { label: 'Alphabetic (e.g. 1A, 1B)', value: 'Alphabetic' }
                                ]}
                                value={[
                                    { label: 'Floor-Prefixed (e.g. 101)', value: 'Floor-Prefixed' },
                                    { label: 'Floor-Prefixed (e.g. 1001, 1002)', value: 'Floor-Prefixed-3Digit' },
                                    { label: 'Sequential (e.g. 01, 02)', value: 'Sequential' },
                                    { label: 'Alphabetic (e.g. 1A, 1B)', value: 'Alphabetic' }
                                ].find(o => o.value === bulkConfig.unitNumbering) || null}
                                onChange={(opt) => {
                                    setBulkConfig({ ...bulkConfig, unitNumbering: opt.value });
                                    if (bulkErrors.unitNumbering) setBulkErrors(prev => ({ ...prev, unitNumbering: null }));
                                }}
                                error={!!bulkErrors.unitNumbering}
                            />
                            {bulkErrors.unitNumbering && <p className="text-red-500 text-[10px] mt-1">{bulkErrors.unitNumbering}</p>}
                        </div>
                    </div>

                    <div className="flex justify-between items-center mt-6">
                        <p className="text-[10px] text-gray-500">Note: Units inside Parking and Service floors must be managed manually.</p>
                        <button
                            onClick={handleBulkGenerate}
                            className="px-6 py-2.5 bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 text-white rounded-lg text-xs font-bold hover:opacity-90 transition-all shadow-md flex items-center justify-center gap-2"
                        >
                            <FontAwesomeIcon icon={faWandMagicSparkles} /> Auto-Generate
                        </button>
                    </div>

                    {/* Dynamic Wing Inputs before generating */}
                    {bulkConfig.wingsConfig.length > 0 && (
                        <div className="mt-6 pt-6 border-t border-blue-100/50 space-y-4">
                            <h4 className="text-xs font-bold text-gray-600 dark:text-textprimary mb-2">Configure Initial Wings</h4>
                            {bulkConfig.wingsConfig.map((wc, idx) => (
                                <div key={idx} className="flex flex-col sm:flex-row gap-4 items-end bg-white dark:bg-dashboardbg p-3 rounded-lg border border-blue-50 dark:border-bordercolor">
                                    <div className="flex-1 w-full">
                                        <label className="block text-[10px] font-bold text-gray-500 dark:text-textsecondary uppercase tracking-wide mb-1">Wing {idx + 1} Name <span className="text-red-500">*</span></label>
                                        <input
                                            value={wc.name}
                                            onChange={(e) => {
                                                const newConfig = [...bulkConfig.wingsConfig];
                                                newConfig[idx].name = e.target.value;
                                                setBulkConfig({ ...bulkConfig, wingsConfig: newConfig });
                                                if (bulkErrors[`wingName_${idx}`]) setBulkErrors(prev => ({ ...prev, [`wingName_${idx}`]: null }));
                                            }}
                                            className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${bulkErrors[`wingName_${idx}`] ? 'border-red-500' : 'border-gray-200'}`} type="text" />
                                        {bulkErrors[`wingName_${idx}`] && <p className="text-red-500 text-[10px] mt-1">{bulkErrors[`wingName_${idx}`]}</p>}
                                    </div>
                                    <div className="flex-1 w-full">
                                        <label className="block text-[10px] font-bold text-gray-500 dark:text-textsecondary uppercase tracking-wide mb-1">Wing {idx + 1} Status</label>
                                        <CustomSelect
                                            options={[{ label: 'Active', value: 'Active' }, { label: 'Inactive', value: 'Inactive' }]}
                                            value={{ label: wc.status, value: wc.status }}
                                            onChange={(opt) => {
                                                const newConfig = [...bulkConfig.wingsConfig];
                                                newConfig[idx].status = opt.value;
                                                setBulkConfig({ ...bulkConfig, wingsConfig: newConfig });
                                            }}
                                        />
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* 3. Hierarchy Viewer Section */}
                <div className="flex items-center justify-between mb-4">
                    <div>
                        <h3 className="text-sm font-bold text-gray-800 dark:text-textprimary tracking-tight">Structural Hierarchy</h3>
                        <p className="text-xs text-gray-500 mt-1">Review and modify the generated structure below.</p>
                    </div>
                </div>

                {formData.wings.length === 0 && (
                    <div className="text-center py-12 bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-2xl mb-8">
                        <FontAwesomeIcon icon={faBuilding} className="text-4xl text-gray-200 mb-3" />
                        <h4 className="text-sm font-bold text-gray-500">No Structure Yet</h4>
                        <p className="text-xs text-gray-400 mt-1">Use the Bulk Generator above to create wings.</p>
                    </div>
                )}

                {/* Wings Mapping */}
                <div className="space-y-4 mb-8">
                    {formData.wings.map((wing, wIdx) => (
                        <div key={wing.id} className="bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-xl shadow-sm overflow-hidden transition-all duration-300">
                            {/* Wing Header */}
                            <div className="bg-slate-50 dark:bg-actionitembg px-5 py-3 border-b border-gray-200 dark:border-bordercolor flex justify-between items-center cursor-pointer" onClick={() => toggleWing(wIdx)}>
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-lg bg-[#FFEFEF] dark:bg-actionitembg text-[#E31F26] dark:text-textprimary flex items-center justify-center">
                                        <FontAwesomeIcon icon={faBuilding} className="text-sm" />
                                    </div>
                                    <div>
                                        <div className="text-sm font-bold text-gray-800 dark:text-textprimary">{wing.wingName || "Untitled Wing"}</div>
                                        <div className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{wing.floors.length} Floors Included</div>
                                    </div>
                                </div>
                                <div className="flex items-center gap-3">
                                    <button
                                        onClick={(e) => { e.stopPropagation(); removeWing(wIdx); }}
                                        className="w-8 h-8 flex items-center justify-center bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor text-red-500 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shadow-sm"
                                    >
                                        <FontAwesomeIcon icon={faTrash} className="text-xs" />
                                    </button>
                                    <button
                                        onClick={(e) => { e.stopPropagation(); addFloor(wIdx); }}
                                        className="px-3 py-1.5 bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor text-gray-700 dark:text-textprimary rounded-lg text-xs font-bold hover:bg-gray-50 transition-colors shadow-sm"
                                    >
                                        <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add Floor
                                    </button>
                                    <FontAwesomeIcon icon={wing.isExpanded ? faChevronUp : faChevronDown} className="text-gray-400 ml-2" />
                                </div>
                            </div>

                            {/* Wing Content (Floors) */}
                            {wing.isExpanded && (
                                <div className="p-5 bg-white dark:bg-dashboardbg">
                                    {/* Floors Mapping */}
                                    <div className="space-y-4">
                                        {wing.floors.map((floor, fIdx) => (
                                            <div key={floor.id} className="bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor rounded-lg shadow-sm overflow-hidden ml-4">
                                                <div className="bg-gray-50 dark:bg-dashboardbg px-4 py-2 border-b border-gray-200 dark:border-bordercolor flex justify-between items-center cursor-pointer" onClick={() => toggleFloor(wIdx, fIdx)}>
                                                    <div className="flex items-center gap-3">
                                                        <div className={`w-6 h-6 rounded-md flex items-center justify-center ${floor.floorType === 'Parking' ? 'bg-amber-100 text-amber-600' :
                                                            floor.floorType === 'Service' ? 'bg-emerald-100 text-emerald-600' :
                                                                'bg-slate-200 text-slate-600'
                                                            }`}>
                                                            <FontAwesomeIcon icon={faLayerGroup} className="text-[10px]" />
                                                        </div>
                                                        <div className="text-xs font-bold text-gray-800 dark:text-textprimary">
                                                            {floor.floorName ? `Floor ${floor.floorName}` : `Floor #${fIdx + 1}`}
                                                            <span className="ml-2 px-1.5 py-0.5 rounded bg-gray-100 text-[10px] text-gray-500 uppercase tracking-wide">{floor.floorType}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex items-center gap-3">
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); removeFloor(wIdx, fIdx); }}
                                                            className="w-7 h-7 flex items-center justify-center bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor text-red-500 rounded-md hover:bg-red-50 dark:hover:bg-red-500/10 transition-colors shadow-sm"
                                                            title="Remove Floor"
                                                        >
                                                            <FontAwesomeIcon icon={faTrash} className="text-[10px]" />
                                                        </button>
                                                        <button
                                                            onClick={(e) => { e.stopPropagation(); addUnit(wIdx, fIdx); }}
                                                            className="px-2.5 py-1 bg-white dark:bg-actionitembg border border-gray-200 dark:border-bordercolor text-gray-600 dark:text-textprimary rounded-md text-[10px] font-bold hover:bg-gray-50 transition-colors shadow-sm"
                                                        >
                                                            <FontAwesomeIcon icon={faPlus} className="mr-1" /> Add Unit
                                                        </button>
                                                        <FontAwesomeIcon icon={floor.isExpanded ? faChevronUp : faChevronDown} className="text-gray-400 text-xs ml-1" />
                                                    </div>
                                                </div>

                                                {floor.isExpanded && (
                                                    <div className="p-4">
                                                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                                                            <div>
                                                                <label className="block text-[10px] font-bold text-gray-500 dark:text-textsecondary uppercase tracking-wide mb-1">Floor Number / Name <span className="text-red-500">*</span></label>
                                                                <input
                                                                    value={floor.floorName}
                                                                    onChange={(e) => {
                                                                        const newWings = [...formData.wings];
                                                                        newWings[wIdx].floors[fIdx].floorName = e.target.value;
                                                                        setFormData({ ...formData, wings: newWings });
                                                                        if (errors[`floorName_${wIdx}_${fIdx}`]) {
                                                                            setErrors(prev => ({ ...prev, [`floorName_${wIdx}_${fIdx}`]: null }));
                                                                        }
                                                                    }}
                                                                    className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all ${errors[`floorName_${wIdx}_${fIdx}`] ? 'border-red-500' : 'border-gray-200'}`}
                                                                    type="text" placeholder="e.g. 1st"
                                                                />
                                                                {errors[`floorName_${wIdx}_${fIdx}`] && <p className="text-red-500 text-[10px] mt-1">{errors[`floorName_${wIdx}_${fIdx}`]}</p>}
                                                            </div>
                                                            <div>
                                                                <label className="block text-[10px] font-bold text-gray-500 dark:text-textsecondary uppercase tracking-wide mb-1">Floor Type</label>
                                                                <CustomSelect
                                                                    options={[
                                                                        { label: 'Normal', value: 'Normal' },
                                                                        { label: 'Parking', value: 'Parking' },
                                                                        { label: 'Service', value: 'Service' }
                                                                    ]}
                                                                    value={{ label: floor.floorType, value: floor.floorType }}
                                                                    onChange={(opt) => {
                                                                        const newWings = [...formData.wings];
                                                                        newWings[wIdx].floors[fIdx].floorType = opt.value;
                                                                        setFormData({ ...formData, wings: newWings });
                                                                    }}
                                                                />
                                                            </div>
                                                        </div>

                                                        {/* Units Mapping */}
                                                        {floor.units.length > 0 ? (
                                                            <div className="mt-4 pt-4 border-t border-gray-100">
                                                                <label className="block text-[10px] font-bold text-gray-500 dark:text-textsecondary uppercase tracking-wide mb-3">Units in this Floor ({floor.units.length})</label>
                                                                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
                                                                    {floor.units.map((unit, uIdx) => (
                                                                        <div key={unit.id} className="bg-white dark:bg-dashboardbg border border-gray-200 dark:border-bordercolor rounded-lg p-3 shadow-sm relative group">
                                                                            <FontAwesomeIcon icon={faDoorOpen} className="text-gray-100 text-3xl absolute right-2 bottom-2 z-0" />
                                                                            <div className="relative z-10">
                                                                                <input
                                                                                    value={unit.unitNumber}
                                                                                    onChange={(e) => {
                                                                                        const newWings = [...formData.wings];
                                                                                        newWings[wIdx].floors[fIdx].units[uIdx].unitNumber = e.target.value;
                                                                                        setFormData({ ...formData, wings: newWings });
                                                                                        if (errors[`unitNumber_${wIdx}_${fIdx}_${uIdx}`]) {
                                                                                            setErrors(prev => ({ ...prev, [`unitNumber_${wIdx}_${fIdx}_${uIdx}`]: null }));
                                                                                        }
                                                                                    }}
                                                                                    className={`w-full px-3 py-2 border rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all font-bold text-blue-600 mb-2 ${errors[`unitNumber_${wIdx}_${fIdx}_${uIdx}`] ? 'border-red-500' : 'border-gray-200'}`}
                                                                                    placeholder="Unit No. *"
                                                                                />
                                                                                {errors[`unitNumber_${wIdx}_${fIdx}_${uIdx}`] && <p className="text-red-500 text-[9px] mt-0.5 leading-tight mb-2">{errors[`unitNumber_${wIdx}_${fIdx}_${uIdx}`]}</p>}
                                                                                <input
                                                                                    value={unit.unitName}
                                                                                    onChange={(e) => {
                                                                                        const newWings = [...formData.wings];
                                                                                        newWings[wIdx].floors[fIdx].units[uIdx].unitName = e.target.value;
                                                                                        setFormData({ ...formData, wings: newWings });
                                                                                    }}
                                                                                    className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white dark:bg-actionitembg dark:text-textprimary focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all placeholder:text-gray-400"
                                                                                    placeholder="Unit Name (Opt)"
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="mt-4 pt-4 border-t border-gray-100 flex items-center justify-between">
                                                                <p className="text-[10px] font-bold text-gray-400">No units added yet. Manage manually.</p>
                                                            </div>
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* Footer Actions */}
            <div className="flex justify-between mt-4 border-t border-gray-200 pt-6">
                <button
                    onClick={() => navigate('/zentcomm/site-management/site')}
                    className="px-4 py-2 border border-gray-300 bg-white rounded-xl text-xs font-bold flex items-center gap-2 text-gray-700 hover:bg-gray-50 transition-all shadow-sm">
                    <FontAwesomeIcon icon={faAngleLeft} /> Cancel
                </button>

                <button
                    onClick={handleSubmit}
                    disabled={isLoading}
                    className="px-6 py-2 rounded-xl text-xs font-bold flex items-center gap-2 shadow-sm hover:shadow-md active:scale-95 transition-all duration-200"
                    style={{ backgroundColor: secondaryColor(), color: primaryColor() }}
                    onMouseEnter={(e) => {
                        e.currentTarget.style.backgroundColor = primaryColor();
                        e.currentTarget.style.color = '#FFFFFF';
                    }}
                    onMouseLeave={(e) => {
                        e.currentTarget.style.backgroundColor = secondaryColor();
                        e.currentTarget.style.color = primaryColor();
                    }}
                >
                    {isLoading ? <FontAwesomeIcon icon={faSpinner} className="animate-spin" /> : (
                        <>Save Entire Structure <FontAwesomeIcon icon={faAngleRight} /></>
                    )}
                </button>
            </div>
        </div>
    );
};

export default SiteHierarchyBuilder;
